import {HttpErrorResponse} from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {catchError, map, startWith} from 'rxjs/operators';

export interface ViewState<T> {
  loading: boolean;
  error: boolean;
  data: T | null;
}

/**
 * RxJS operator that wraps an Observable<T> into an Observable<ViewState<T>>.
 *
 * Emits { loading: true } immediately, then { data: T } on success,
 * or { error: true } on failure.
 *
 * Usage:
 *   this.state$ = source$.pipe(toViewState());
 *
 * @param errorPredicate Optional function to determine if an error should be flagged.
 *   Defaults to always true. Use e.g. `err => err.status !== 404` to ignore 404s.
 */
export function toViewState<T>(errorPredicate?: (err: HttpErrorResponse) => boolean) {
  return (source$: Observable<T>): Observable<ViewState<T>> =>
    source$.pipe(
      map((data: T) => ({loading: false, error: false, data} as ViewState<T>)),
      catchError(err => of({
        loading: false,
        error: errorPredicate ? errorPredicate(err) : true,
        data: null
      } as ViewState<T>)),
      startWith({loading: true, error: false, data: null} as ViewState<T>)
    );
}
